import { format, parseISO, isValid, differenceInDays, differenceInHours } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';

/**
 * Detecta se uma string é uma data ISO válida
 */
const isISODate = (str: string): boolean => {
  if (typeof str !== 'string') return false;
  
  // Pattern para ISO 8601: YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss...
  const isoPattern = /^\d{4}-\d{2}-\d{2}(T|\s)/;
  if (!isoPattern.test(str)) return false;
  
  try {
    const date = parseISO(str);
    return isValid(date);
  } catch {
    return false;
  }
};

/**
 * Determina o melhor formato de data baseado no range dos dados
 */
const getDateFormatByRange = (dates: Date[]): {
  format: string;
  locale: typeof ptBR | typeof enUS;
} => {
  if (dates.length < 2) {
    return { format: 'd MMM yyyy, HH:mm', locale: ptBR };
  }

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];
  
  const hoursDiff = Math.abs(differenceInHours(lastDate, firstDate));
  const daysDiff = Math.abs(differenceInDays(lastDate, firstDate));

  // Determinar locale baseado no idioma do navegador
  const language = navigator.language || 'pt-BR';
  const locale = language.startsWith('en') ? enUS : ptBR;

  // Range de horas (< 24h): mostrar hora:minuto
  if (hoursDiff < 24) {
    return { format: 'HH:mm', locale };
  }
  
  // Range de dias (< 31 dias): mostrar dia e mês
  if (daysDiff < 31) {
    return { format: 'd MMM', locale };
  }
  
  // Range de meses (< 365 dias): mostrar mês e ano
  if (daysDiff < 365) {
    return { format: 'MMM yyyy', locale };
  }
  
  // Range de anos: mostrar ano completo
  return { format: 'yyyy', locale };
};

/**
 * Formata labels de forma inteligente
 * - Se forem datas ISO, aplica formatação adaptativa
 * - Caso contrário, retorna as labels originais
 */
export const formatChartLabels = (labels: string[]): string[] => {
  if (!labels || labels.length === 0) return labels;

  // Verificar se todas as labels são datas ISO
  const allDates = labels.every(label => isISODate(label));
  
  if (!allDates) {
    return labels; // Retornar originais se não forem datas
  }

  // Parse todas as datas
  const parsedDates = labels.map(label => parseISO(label));

  // Determinar melhor formato
  const { format: dateFormat, locale } = getDateFormatByRange(parsedDates);

  // Formatar todas as labels
  return parsedDates.map(date => format(date, dateFormat, { locale }));
};

/**
 * Formata uma única label de data
 */
export const formatSingleDateLabel = (label: string, lang: string = 'pt'): string => {
  if (!isISODate(label)) return label;

  try {
    const date = parseISO(label);
    const locale = lang.startsWith('en') ? enUS : ptBR;
    
    // Formato padrão para label única
    return format(date, 'd MMM yyyy, HH:mm', { locale });
  } catch {
    return label;
  }
};
